import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import {
  Topo, Periodo, Indicadores, Indicador,
  SeloEstado, ENCERRADO, Erro, Carregando, Vazio,
} from '../componentes/ui'
import './meu-painel.css'

/**
 * O painel de quem atende.
 *
 * Esta é a tela do celular, e o recorte veio de evidência: o app do
 * concorrente entrega exatamente quatro coisas ao profissional —
 * agenda, comissão, ficha e fotos — e mesmo assim tira 2,6 de 5 na
 * loja, com "trava toda hora" e "fecha no meio do uso" como queixas
 * mais repetidas. Não é falta de recurso; é o básico não funcionar.
 *
 * Então aqui a agenda é uma LISTA, não uma grade. Grade é boa num
 * monitor de recepção; entre um atendimento e outro, o que a pessoa
 * quer saber é "quem é o próximo, a que horas, em qual sala".
 */

type Item = {
  agendamento_id: string
  inicio: string; fim: string
  status: string; confirmacao: string
  paciente_id: string | null; paciente: string | null
  paciente_telefone: string | null
  paciente_financeiro: 'OK' | 'PENDENTE' | 'VENCIDO' | null
  procedimento: string | null
  sala: string | null
  valor_centavos: string | null
  comissao_centavos: string | null
}

type Agenda = {
  profissional_id: string | null
  de: string; ate: string
  itens: Item[]
  totais: {
    atendimentos: string; bruto_centavos: string
    comissao_centavos: string; confirmados: string; concluidos: string
  }
}

type Comissao = {
  competencia: string
  atendimentos: number | string
  bruto_centavos: string
  comissao_centavos: string
  comissao_liquidada_centavos: string
}

const brl = (c: string | number | null) =>
  (Number(c ?? 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const hoje = () => new Date().toISOString().slice(0, 10)
const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

const mesExtenso = (iso: string) =>
  new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })

const diaExtenso = (iso: string) => {
  const t = new Date(iso + 'T12:00:00')
    .toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })
  return t.charAt(0).toUpperCase() + t.slice(1)
}

const somarDias = (iso: string, n: number) => {
  const d = new Date(iso + 'T12:00:00')
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

export function MeuPainel() {
  const [dia, setDia] = useState(hoje)
  const [agenda, setAgenda] = useState<Agenda | null>(null)
  const [comissao, setComissao] = useState<Comissao | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setErro(null)
    try {
      const [a, c] = await Promise.all([
        api<Agenda>(`/v1/painel/minha-agenda?de=${dia}&ate=${dia}`),
        api<Comissao>('/v1/painel/minha-comissao'),
      ])
      setAgenda(a); setComissao(c)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [dia])
  useEffect(() => { void carregar() }, [carregar])

  const itens = agenda?.itens ?? []

  /* O "próximo" é o primeiro que ainda não terminou. Se o dia já
     passou inteiro, não existe próximo — e é melhor não destacar nada
     do que destacar um atendimento encerrado. */
  const agora = Date.now()
  const proximo = dia === hoje()
    ? itens.find((i) => !ENCERRADO.has(i.status) && new Date(i.fim).getTime() >= agora)
    : undefined

  const semVinculo = agenda && agenda.profissional_id === null

  return (
    <div className="mp">
      <Topo titulo="Meu dia" subtitulo={diaExtenso(dia)}>
        <Periodo
          rotuloAtual="Hoje"
          estaNoAgora={dia === hoje()}
          aoAnterior={() => setDia(somarDias(dia, -1))}
          aoProximo={() => setDia(somarDias(dia, 1))}
          aoAgora={() => setDia(hoje())}
        >
          <label htmlFor="mp-dia" className="oculto-visual">Escolher dia</label>
          <input id="mp-dia" className="entrada mp__data" type="date"
                 value={dia} onChange={(e) => setDia(e.target.value)} />
        </Periodo>
      </Topo>

      <Erro mensagem={erro} />

      {/* Um usuário que não é profissional cadastrado não tem agenda
          própria — e dizer "nenhum atendimento" nesse caso mente sobre
          a causa. */}
      {semVinculo && (
        <div className="aviso aviso--atencao">
          Sua conta não está vinculada a um profissional do corpo clínico, então não há
          agenda pessoal para mostrar. Peça a quem administra a clínica para fazer o
          vínculo em Configurações.
        </div>
      )}

      {carregando ? <Carregando altura={96} /> : agenda && !semVinculo && (
        <>
          <Indicadores>
            <Indicador
              rotulo="Atendimentos hoje"
              valor={agenda.totais.atendimentos}
              ajuda={`${agenda.totais.confirmados} confirmado(s) · ${agenda.totais.concluidos} concluído(s)`}
            />
            <Indicador
              rotulo="A receber no dia" tom="ok"
              valor={brl(agenda.totais.comissao_centavos)}
              ajuda={`sobre ${brl(agenda.totais.bruto_centavos)} atendidos`}
            />
            {comissao && (
              <Indicador
                destaque tom="ok"
                rotulo={`No mês · ${mesExtenso(comissao.competencia)}`}
                valor={brl(comissao.comissao_centavos)}
                /* Recebido e liquidado são números diferentes, e a
                   diferença é dinheiro que ainda não caiu. */
                ajuda={`${
                  Number(comissao.comissao_liquidada_centavos) > 0
                    ? `${brl(comissao.comissao_liquidada_centavos)} já liquidado`
                    : 'nada liquidado ainda'
                } · ${comissao.atendimentos} atendimento(s)`}
              />
            )}
          </Indicadores>

          {proximo && (
            <section className="mp__proximo" aria-label="Próximo atendimento">
              <span className="mp__proximo-rot">Próximo</span>
              <div className="mp__proximo-corpo">
                <strong className="mp__proximo-hora mono">{hora(proximo.inicio)}</strong>
                <div>
                  <strong className="mp__proximo-nome">{proximo.paciente ?? 'Sem paciente'}</strong>
                  <p className="ajuda">
                    {proximo.procedimento ?? 'Sem procedimento'}
                    {proximo.sala && ` · ${proximo.sala}`}
                  </p>
                </div>
              </div>
              {/*
                Só `tel:`, deliberadamente. O caminho óbvio seria um link
                `wa.me/55<telefone>` — e é o que o concorrente faz — mas
                isso põe o telefone do paciente na URL de um domínio que
                não é nosso, e manda o profissional conversar FORA do
                sistema, sem registro.

                Quando o canal WhatsApp estiver aprovado na Meta, este
                botão dispara pelo número da própria clínica e a mensagem
                fica no histórico do paciente.
              */}
              {proximo.paciente_telefone && (
                <div className="mp__proximo-acoes">
                  <a className="botao botao--pequeno"
                     href={`tel:${proximo.paciente_telefone.replace(/\D/g, '')}`}>
                    Ligar para o paciente
                  </a>
                </div>
              )}
            </section>
          )}

          <section className="mp__lista" aria-label="Atendimentos do dia">
            {itens.length === 0 ? (
              <Vazio
                mensagem="Nenhum atendimento neste dia."
                sugestao="Use as setas acima para ver outro dia."
              />
            ) : itens.map((i) => (
              <article
                key={i.agendamento_id}
                className={`mp__item ${ENCERRADO.has(i.status) ? 'is-encerrado' : ''} ${
                  proximo?.agendamento_id === i.agendamento_id ? 'is-proximo' : ''}`}
              >
                <div className="mp__hora">
                  <strong className="mono">{hora(i.inicio)}</strong>
                  <span className="ajuda mono">{hora(i.fim)}</span>
                </div>

                <div className="mp__corpo">
                  <div className="mp__linha1">
                    <strong className="mp__nome">{i.paciente ?? 'Sem paciente'}</strong>
                    <SeloEstado estado={i.status} />
                  </div>
                  <p className="ajuda mp__meta">
                    {i.procedimento ?? 'Sem procedimento'}
                    {i.sala && <> · <span className="mp__sala">{i.sala}</span></>}
                  </p>
                  {/* A situação financeira só aparece quando é problema.
                      Um selo "OK" em toda linha vira ruído e some
                      justamente quando importa. */}
                  {i.paciente_financeiro === 'VENCIDO' && (
                    <span className="selo selo--erro">pendência vencida</span>
                  )}
                  {i.paciente_financeiro === 'PENDENTE' && (
                    <span className="selo selo--atencao">tem pendência</span>
                  )}
                </div>

                <div className="mp__dir">
                  {i.comissao_centavos && Number(i.comissao_centavos) > 0 && (
                    <strong className="mp__ganho mono">{brl(i.comissao_centavos)}</strong>
                  )}
                  {i.paciente_telefone && !ENCERRADO.has(i.status) && (
                    <a className="mp__zap"
                       href={`tel:${i.paciente_telefone.replace(/\D/g, '')}`}
                       aria-label={`Ligar para ${i.paciente ?? 'o paciente'}`}>Ligar</a>
                  )}
                </div>
              </article>
            ))}
          </section>
        </>
      )}
    </div>
  )
}
